import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

import Input from "../../components/Form/Input/input";
import { Select } from "../../components/Form/Input/select";
import Label from "../../components/Form/Label";
import PhoneNumberInput from "../../components/Form/PhoneNumberInput";
import { FormGroup } from "../../components/Form/styles";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { Skeleton } from "../../components/Skeleton";
import { useAccountCurrency } from "../../hooks/AccountCurrency";
import {
  AgendaBooking,
  AgendaService,
  AgendaSettings,
  AgendaSlot,
  createBooking,
  updateBooking,
  errorCodeOf,
  getAgenda,
  listServices,
  listSlots,
} from "../../services/agenda";
import { formatCents } from "./format";
import { FieldError } from "./styles";
import { TimePicker } from "./TimePicker";

const prefix = "pages.agenda.bookings.new";

const DAYS = 7;

// a day as a date at noon UTC: added to and formatted in UTC, it never slips to the day before
const noon = (day: string) => new Date(`${day}T12:00:00Z`);
const addDays = (day: string, days: number) => {
  const date = noon(day);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

type Props = {
  siteId: string;
  date: string;
  /** A booking to change instead of a new one: everything starts as it is. */
  booking?: AgendaBooking;
  onCreated: (booking: AgendaBooking) => void;
  onExit: () => void;
};

/**
 * The owner's booking: any service (also one hidden from the page), any day and any time, even a busy one; the
 * customer hears of it as of any other. The day on the week's tiles, the time on the day's grid (free, or busy and
 * still pickable, with a warning) or typed ("Outro…"); then the customer.
 */
export const NewBookingModal = ({ siteId, date: initialDate, booking, onCreated, onExit }: Props) => {
  const { t, i18n } = useTranslation();
  const { currency } = useAccountCurrency();
  const [services, setServices] = useState<AgendaService[]>();
  const [agenda, setAgenda] = useState<AgendaSettings>();
  const [service, setService] = useState(booking?.service.id ?? "");
  const [start, setStart] = useState(booking?.date ?? initialDate);
  const [date, setDate] = useState(booking?.date ?? initialDate);
  const [slots, setSlots] = useState<AgendaSlot[]>();
  // what couldn't be loaded: not the same as there being none
  const [servicesFailed, setServicesFailed] = useState(false);
  const [slotsFailed, setSlotsFailed] = useState(false);
  const [time, setTime] = useState(booking?.time ?? "");
  const [typing, setTyping] = useState(false);
  const [name, setName] = useState(booking?.customer.name ?? "");
  const [phone, setPhone] = useState(booking?.customer.phone ?? "");
  const [email, setEmail] = useState(booking?.customer.email ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listServices(siteId)
      .then(items => {
        setServices(items);
      })
      .catch(() => {
        setServices([]);
        setServicesFailed(true);
      });
    getAgenda(siteId)
      .then(setAgenda)
      .catch(() => undefined);
  }, [siteId]);

  // the day's grid for the service
  useEffect(() => {
    if (!service) return;
    let current = true;
    setSlots(undefined);
    setSlotsFailed(false);
    listSlots(siteId, service, date)
      .then(items => current && setSlots(items))
      .catch(() => {
        if (!current) return;
        setSlots([]);
        setSlotsFailed(true);
      });
    return () => {
      current = false;
    };
  }, [siteId, service, date]);

  // "Corte de cabelo | 30 min - R$ 50,00": its time and price beside its name
  const serviceLabel = (item: AgendaService) => {
    const details = [
      t("pages.agenda.services.minutes", { count: item.durationMinutes }),
      formatCents(item.priceCents, currency),
    ]
      .filter(Boolean)
      .join(" - ");
    return `${item.name} | ${details}`;
  };

  // a day without hours or blocked: dimmed, still pickable (the owner books any day)
  const isClosed = (day: string) =>
    !!agenda && ((agenda.week[String(noon(day).getUTCDay())] ?? []).length === 0 || agenda.blockedDates.includes(day));

  const chooseDay = (day: string) => {
    setDate(day);
    setTime("");
    setTyping(false);
  };

  const moveWeek = (weeks: number) => {
    const next = addDays(start, weeks * DAYS);
    setStart(next);
    chooseDay(next);
  };

  // a booking being changed: its own time isn't "busy" for it
  const isOwnTime = (slot: AgendaSlot) => !!booking && date === booking.date && slot.time === booking.time;
  const grid = slots?.map(slot => (isOwnTime(slot) ? { ...slot, busy: false } : slot));
  const chosenSlot = grid?.find(slot => slot.time === time);
  const hasGrid = !!grid && grid.length > 0;

  // a time that isn't on the day's grid (typed before, or the hours changed): shown in "Outro…"
  useEffect(() => {
    if (slots && time && !slots.some(slot => slot.time === time)) setTyping(true);
    // only when the grid comes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const save = async () => {
    // a booking being changed may keep a service that was deleted meanwhile
    if ((!service && !booking) || !date || !/^\d{2}:\d{2}$/.test(time) || !name.trim()) {
      setProblem("missing");
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      const fields = { "agenda-name": name.trim(), "agenda-phone": phone.trim(), "agenda-email": email.trim() };
      onCreated(
        booking
          ? await updateBooking(siteId, booking.id, { ...(service && { service }), date, time, fields })
          : await createBooking(siteId, { service, date, time, fields })
      );
    } catch (error) {
      setProblem(errorCodeOf(error) === "FORM_VALIDATION_ERROR" ? "fields" : "save");
    } finally {
      setSaving(false);
    }
  };

  const format = (day: string, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(i18n.language, { ...options, timeZone: "UTC" }).format(noon(day));

  return (
    <Modal
      title={t(booking ? `${prefix}.editTitle` : `${prefix}.title`)}
      subtitle={t(booking ? `${prefix}.editSubtitle` : `${prefix}.subtitle`)}
      escapeExit
      busy={saving}
      onExit={onExit}
      error={problem ? t(`${prefix}.errors.${problem}`) : undefined}
      footer={
        <>
          {/* not while it saves: the request is on its way and would still create the booking */}
          <ModalSecondaryButton type="button" disabled={saving} onClick={onExit}>
            {t(`${prefix}.cancel`)}
          </ModalSecondaryButton>
          <ModalButton loading={saving} onClick={save}>
            {t(booking ? `${prefix}.saveEdit` : `${prefix}.save`)}
          </ModalButton>
        </>
      }>
      <Part>
        {/* the group stacks in reverse (label on top): the control comes first */}
        <FormGroup>
          <Select
            id="new-booking-service"
            value={service}
            onChange={event => {
              setService(event.target.value);
              setTime("");
            }}>
            <option value="" disabled>
              {t(`${prefix}.pickService`)}
            </option>
            {(services ?? []).map(item => (
              <option key={item.id} value={item.id}>
                {serviceLabel(item)}
              </option>
            ))}
          </Select>
          <Label htmlFor="new-booking-service" text={t(`${prefix}.service`)} ignoreLengthInfo />
        </FormGroup>
        {servicesFailed && <FieldError>{t(`${prefix}.errors.services`)}</FieldError>}
        {services && services.length === 0 && !servicesFailed && <FieldError>{t(`${prefix}.noServices`)}</FieldError>}

        <div>
          <Caption id="new-booking-day">{t(`${prefix}.date`)}</Caption>
          <Week role="group" aria-labelledby="new-booking-day">
            <button
              type="button"
              className="arrow"
              aria-label={t("pages.agenda.bookings.previous")}
              onClick={() => moveWeek(-1)}>
              ‹
            </button>
            {Array.from({ length: DAYS }, (_, index) => addDays(start, index)).map(day => (
              <DayTile
                key={day}
                type="button"
                $selected={day === date}
                $closed={isClosed(day)}
                aria-pressed={day === date}
                onClick={() => chooseDay(day)}>
                <small>{format(day, { weekday: "short" }).replace(".", "")}</small>
                <strong>{`${day.slice(8, 10)}/${day.slice(5, 7)}`}</strong>
              </DayTile>
            ))}
            <button
              type="button"
              className="arrow"
              aria-label={t("pages.agenda.bookings.next")}
              onClick={() => moveWeek(1)}>
              ›
            </button>
          </Week>
        </div>

        <div>
          {/* the label, and on its right what the times' looks mean */}
          <CaptionRow>
            <Caption id="new-booking-time">{t(`${prefix}.time`)}</Caption>
            {hasGrid && (
              <Legend aria-hidden="true">
                <span>
                  <i />
                  {t(`${prefix}.free`)}
                </span>
                <span>
                  <i className="busy" />
                  {t(`${prefix}.busy`)}
                </span>
              </Legend>
            )}
          </CaptionRow>
          {!service && !booking ? (
            <Hint>{t(`${prefix}.serviceFirst`)}</Hint>
          ) : !service ? (
            // a booking whose service was deleted keeps what was booked: no service to ask the day's times for
            <TypedTime>
              <label>{t(`${prefix}.typedTime`)}</label>
              <TimePicker size="field" label={t(`${prefix}.typedTime`)} value={time} onChange={setTime} />
            </TypedTime>
          ) : !slots ? (
            <Times aria-busy="true">
              {Array.from({ length: 8 }, (_, index) => (
                <Skeleton key={index} className="h-[38px] rounded-full" />
              ))}
            </Times>
          ) : (
            <>
              {!hasGrid && <Hint>{slotsFailed ? t(`${prefix}.errors.slots`) : t(`${prefix}.noHours`)}</Hint>}
              <Times role="group" aria-labelledby="new-booking-time">
                {(grid ?? []).map(slot => (
                  <TimePill
                    key={slot.time}
                    type="button"
                    $busy={slot.busy}
                    $selected={!typing && time === slot.time}
                    aria-pressed={!typing && time === slot.time}
                    aria-label={slot.busy ? t(`${prefix}.busySlot`, { time: slot.time }) : slot.time}
                    onClick={() => {
                      setTyping(false);
                      setTime(slot.time);
                    }}>
                    {slot.time}
                  </TimePill>
                ))}
                <TimePill
                  type="button"
                  $other
                  $selected={typing || !hasGrid}
                  aria-pressed={typing || !hasGrid}
                  onClick={() => {
                    setTyping(true);
                    setTime("");
                  }}>
                  {t(`${prefix}.otherTime`)}
                </TimePill>
              </Times>
              {!typing && chosenSlot?.busy && <Warning role="status">{t(`${prefix}.busyWarning`)}</Warning>}
              {(typing || !hasGrid) && (
                <TypedTime>
                  <label>{t(`${prefix}.typedTime`)}</label>
                  <TimePicker size="field" label={t(`${prefix}.typedTime`)} value={time} onChange={setTime} />
                </TypedTime>
              )}
            </>
          )}
        </div>
      </Part>

      <Part>
        <FormGroup>
          <Input
            id="new-booking-name"
            type="text"
            label={t(`${prefix}.name`)}
            value={name}
            required
            maxLength={120}
            ignoreLengthInfo
            onChange={event => setName(event.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <PhoneNumberInput id="new-booking-phone" label={t(`${prefix}.phone`)} value={phone} onChange={setPhone} />
        </FormGroup>
        <FormGroup>
          <Input
            id="new-booking-email"
            type="email"
            label={t(`${prefix}.email`)}
            value={email}
            ignoreLengthInfo
            onChange={event => setEmail(event.target.value)}
          />
        </FormGroup>
      </Part>
    </Modal>
  );
};

/** A part of the form (when, then who), a thin line between them. */
const Part = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${FormGroup} {
    margin-bottom: 0;
  }

  & + & {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--color-neutral-base);
  }
`;

const CaptionRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  p {
    margin-bottom: 0;
  }

  div {
    margin-top: 0;
  }
`;

/** The label of the day's and the time's pickers, as a field's. */
const Caption = styled.p`
  margin-bottom: 0.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-neutral-150);
  text-transform: uppercase;
`;

const Week = styled.div`
  display: grid;
  grid-template-columns: 1.5rem repeat(7, minmax(0, 1fr)) 1.5rem;
  gap: 0.375rem;
  align-items: center;

  .arrow {
    height: 2rem;
    font-size: 1.25rem;
    color: var(--color-neutral-150);
    border-radius: 100px;
  }

  .arrow:hover {
    color: var(--color-neutral-white);
  }
`;

const DayTile = styled.button<{ $selected: boolean; $closed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.4375rem 0;
  font-family: Inter, sans-serif;
  color: var(--color-neutral-white);
  background: var(--color-neutral-darkness);
  border: 1px solid transparent;
  border-radius: 0.75rem;
  opacity: ${({ $closed, $selected }) => ($closed && !$selected ? 0.45 : 1)};
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-neutral-base);
  }

  small {
    font-size: 0.6875rem;
    color: var(--color-neutral-150);
    text-transform: capitalize;
  }

  strong {
    font-size: 0.8125rem;
  }

  ${({ $selected }) =>
    $selected &&
    css`
      color: var(--color-neutral-darkness);
      background: var(--color-neutral-white);

      small {
        color: var(--color-neutral-darkness);
      }
    `}
`;

const Times = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.375rem;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

/** A time of the day: free (outlined), busy (dashed and struck), picked (the brand's blue), or "Outro…". */
const TimePill = styled.button<{ $busy?: boolean; $selected?: boolean; $other?: boolean }>`
  height: 38px;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: ${({ $other }) => ($other ? 600 : 700)};
  color: ${({ $busy, $other }) =>
    $busy ? "var(--color-neutral-light)" : $other ? "var(--color-neutral-150)" : "var(--color-neutral-white)"};
  text-decoration: ${({ $busy }) => ($busy ? "line-through" : "none")};
  font-variant-numeric: tabular-nums;
  border: 1px ${({ $busy }) => ($busy ? "dashed" : "solid")} var(--color-neutral-base);
  border-radius: 100px;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-neutral-150);
  }

  ${({ $selected }) =>
    $selected &&
    css`
      color: var(--color-neutral-white);
      text-decoration: none;
      background: var(--color-brand-blue);
      border-color: var(--color-brand-blue);

      &:hover {
        border-color: var(--color-brand-blue);
      }
    `}
`;

const Legend = styled.div`
  display: flex;
  gap: 0.875rem;
  margin-top: 0.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  color: var(--color-neutral-150);

  span {
    display: inline-flex;
    gap: 0.375rem;
    align-items: center;
  }

  i {
    width: 18px;
    height: 12px;
    border: 1px solid var(--color-neutral-base);
    border-radius: 10px;
  }

  i.busy {
    border-style: dashed;
  }
`;

const Hint = styled.p`
  margin-bottom: 0.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  color: var(--color-neutral-150);
`;

const Warning = styled.p`
  margin-top: 0.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  color: #ffb066;
`;

/** "Outro…": the hour and the minutes side by side, as two small lists. */
const TypedTime = styled.div`
  margin-top: 0.75rem;
  font-family: Inter, sans-serif;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--color-neutral-150);
    text-transform: uppercase;
  }
`;
